import { FC, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { path } from 'ramda';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import IProduct, {
  AdditionalCharacteristicsEnum,
  AnimalCategoriesForFeedEnum,
  AnimalFeedTypeEnum,
  ApprovalDietFeedEnum,
  BatteryHazardousMetalEnum,
  BatteryTechnologyEnum,
  BatteryTypeEnum,
  CatchAreaEnum,
  CategoryWeightlossEnum,
  EggWeightClassEnum,
  FishCatchMethodEnum,
  FishProductionMethodEnum,
  FlammableSubtypeEnum,
  HazardStatementsEnum,
  HazardSymbolsEnum,
  IChemicalPhraseFill,
  IFoodlaCategory,
  ILabel,
  IroningEnum,
  LampEnergyClassEnum,
  NutritionalUnit,
  PreparedFormEnum,
  RecyclingTypeEnum,
  SecurityNoticesEnum,
  SignalWordsEnum,
  SunProtectionFactorEnum,
} from 'models/product';
import {
  HAZARD_STATEMENT_PLACEHOLDERS,
  SECURITY_NOTICE_PLACEHOLDERS,
} from 'components/product/ProductUpdateForm/fields/hazard-placeholders';
import { getAllLabelsByCategrory, SUPPLEMENT_SUBSTANCE_UNITS, DAGSDOS_UNITS_MAP } from 'components/constants-ts';
import restEndpoints from 'constants/restEndpoints';
import RetryIcon from '@material-ui/icons/Replay';

import { isBlank } from 'utils/helpers-ts';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { KeycloakContext } from 'components/Secured';
import { IImage } from 'components/images/ImageSorter';

import ChatGptLogo from '../../../assets/chatgpt-logo.png';
import VisionTab from '../tab';
import IProducer from 'models/producer';
import { getProducerName } from 'components/product/ProductUpdateForm/utils';
import {
  isBakeryProducts,
  isBeverages,
  isCereals,
  isChemical,
  isComplementarySupplements,
  isConfectionery,
  isConsumptionReadySnacks,
  isCosmetic,
  isDetergent,
  isElectric,
  isElectricBattery,
  isElectricLamp,
  isFats,
  isFeedCategory,
  isFish,
  isFoodSupplements,
  isFruktOrGreen,
  isGoods,
  isIceCream,
  isJewelry,
  isMeat,
  isMedical,
  isMilk,
  isNewFood,
  isNewNonFood,
  isOTC,
  isOtherFoodstuffs,
  isPharma,
  isSewingKnittingYarn,
  isSpecialFoodNutrition,
  isSpicesSoupsSalads,
  isSunscreen,
  isSweet,
  isToy,
  isWhiteSnus,
} from 'components/product/ProductUpdateForm/fields/category';
import { IFoodlaCategoryOption } from 'models/category';

const fieldNameMap: Record<string, string> = {
  title: 'Produktnamn',
  EAN: 'EAN-kod',
  short_text: 'Produktbeskrivning',
  ingredient_statement: 'Ingrediensförteckning',
  non_food_ingredients: 'Innehållsförteckning',
  animalFoodIngredients: 'Fodersammansättning',
  nutritional_unit: 'Nutrtional unit',
  energi_kj: 'Energi kj',
  energi_kcal: 'Calories',
  fett: 'Fett',
  mattatFett: 'Varav mättat fett',
  kolhydrat: 'Kolhydrat',
  sockerarter: 'Varav sockerarter',
  fiber: 'Fiber',
  protein: 'Protein',
  salt: 'Salt',
  descriptive_size_amount: 'Nettokvantitet (mängd)',
  descriptive_size_unit: 'Nettokvantitet enhet',
  minTemperature: 'Förvaringstemperatur min',
  maxTemperature: 'Förvaringstemperatur max',
  gross_weight_num: 'Bruttovikt (in grams)',
  place_of_item_activity: 'Ursprungsland',
  brand_food: 'Varumärke',
  brand: 'Varumärke',
  purchasingDataSupplierArticleNo: 'Leverantörens artikelnummer',
};

// graphql query will return "null", although fields did not exist in database
export const removeNilProperty = (data?: Record<string, any>) => {
  const result: Record<string, any> = {};

  Object.entries(data || {}).forEach(([key, val]) => {
    if (!isNil(val) && val !== '') result[key] = val;
  });

  return result;
};

export const mapChatGptDataToFill = (
  state: IProduct,
  data: Record<string, any>,
  {
    isFood,
    isChangedBrand,
    producer,
    isCreatingNew,
    rootCategory,
    isSuperuser,
    isApoteaProducer,
    isApoteaProductProducer,
  }: {
    isFood?: boolean;
    isChangedBrand?: boolean;
    producer?: IProducer;
    isCreatingNew?: boolean;
    rootCategory?: IFoodlaCategoryOption;
    isSuperuser?: boolean;
    isApoteaProducer?: boolean;
    isApoteaProductProducer?: boolean;
  }
) => {
  let modifiedCount = 0;
  const isNotStore = !state?.producerIsStore;

  const overWriteStringValue = (stateValue?: string, gptValue?: string): string | undefined => {
    if (isBlank(stateValue)) {
      if (!isBlank(gptValue)) {
        modifiedCount++;
      }
      return gptValue;
    }
    return stateValue;
  };

  const overWriteNumberValue = (
    stateValue?: number,
    gptValue?: number,
    options?: { checkZero?: boolean }
  ): number | undefined => {
    if (isBlank(stateValue)) {
      if (!isNil(gptValue)) {
        modifiedCount++;
      }
      return gptValue;
    }
    if (options?.checkZero) {
      if (stateValue === 0) {
        if (!isNil(gptValue) && gptValue !== 0) {
          modifiedCount++;
        }
        return gptValue;
      }
    }
    return stateValue;
  };

  const overWriteBooleanValue = (stateValue?: boolean, gptValue?: boolean): boolean | undefined => {
    if (isNil(stateValue)) {
      if (!isNil(gptValue)) {
        modifiedCount++;
      }
      return gptValue;
    }
    return stateValue;
  };

  const mergeSearchTerms = (stateValue?: string, gptValue?: string): string | undefined => {
    // If GPT provides no search terms, return state as-is
    if (isBlank(gptValue)) {
      return stateValue;
    }

    // Parse terms preserving original casing
    const existingTerms = (stateValue || '')
      .split(',')
      .map(t => t.trim())
      .filter(t => t !== '');
    const gptTerms = (gptValue || '')
      .split(',')
      .map(t => t.trim())
      .filter(t => t !== '');

    // Deduplicate case-insensitively while preserving original casing
    const seenLowercase = new Set<string>();
    const uniqueTerms: string[] = [];

    // Add GPT terms first (they take precedence)
    for (const term of gptTerms) {
      const lowerTerm = term.toLowerCase();
      if (!seenLowercase.has(lowerTerm)) {
        seenLowercase.add(lowerTerm);
        uniqueTerms.push(term);
      }
    }

    // Add existing terms that aren't duplicates
    for (const term of existingTerms) {
      const lowerTerm = term.toLowerCase();
      if (!seenLowercase.has(lowerTerm)) {
        seenLowercase.add(lowerTerm);
        uniqueTerms.push(term);
      }
    }

    const combinedTerms = uniqueTerms.join(', ');

    // Only count as modified if GPT actually added new terms
    if (gptTerms.some(term => !existingTerms.some(existing => existing.toLowerCase() === term.toLowerCase()))) {
      modifiedCount++;
    }

    return combinedTerms;
  };

  function overWriteEnumValue<T extends unknown>(
    stateValue?: T[keyof T],
    gptValue?: T[keyof T],
    enumObject?: T
  ): T[keyof T] | undefined {
    if (isBlank(stateValue)) {
      const enumVals = Object.keys((enumObject as any) || {}) as unknown as T[keyof T][];
      // Check if valid Enum value
      if (enumVals.includes(gptValue!)) {
        if (!isBlank(gptValue)) {
          modifiedCount++;
        }
        return gptValue;
      } else {
        return stateValue;
      }
    }
    return stateValue;
  }

  function overWriteEnumListValue<T extends any>(
    stateValues?: T[keyof T][],
    gptValue?: T[keyof T] | T[keyof T][],
    enumObject?: T
  ): T[keyof T][] | undefined {
    if (isNil(stateValues) || !stateValues.length) {
      if (isNil(gptValue)) {
        return stateValues;
      }

      const enumVals = Object.keys((enumObject as any) || {}) as unknown as T[keyof T][];
      if (Array.isArray(gptValue)) {
        if (!gptValue.length) {
          return stateValues;
        }
        modifiedCount++;
        // Check if valid Enum value
        return gptValue.filter(value => enumVals.includes(value));
      }

      // Check if valid Enum value
      if (isBlank(gptValue) || !enumVals.includes(gptValue)) {
        return stateValues;
      }
      modifiedCount++;
      return [gptValue];
    }
    return stateValues;
  }

  // Drops fills whose parent code is no longer in the active code array. Used
  // before overWriteSlotFills when AI replaces hazardStatements/securityNotices
  // — without this, fills bound to removed codes would persist as orphan data.
  const dropOrphanFills = (
    fills: IChemicalPhraseFill[] | undefined,
    activeCodes: string[] | undefined
  ): IChemicalPhraseFill[] | undefined => {
    if (!fills || fills.length === 0) return fills;
    if (!activeCodes || activeCodes.length === 0) return [];
    const active = new Set(activeCodes);
    const kept = fills.filter(f => active.has(f.code));
    return kept.length === fills.length ? fills : kept;
  };

  // Validates AI-returned slot fills against the known placeholder spec for each
  // hazard / security code. Drops entries with unselected codes, invalid slot
  // indices, or blank values. Merges per (code, slotIndex): user-filled slots
  // win, AI fills only the slots the user hasn't touched. Caller is expected
  // to first prune orphan fills (via dropOrphanFills) before invoking this.
  const overWriteSlotFills = (
    stateValues: IChemicalPhraseFill[] | undefined,
    gptValue: unknown,
    spec: Record<string, { slots: { optional: boolean }[] }>,
    activeCodes: string[] | undefined
  ): IChemicalPhraseFill[] | undefined => {
    if (!Array.isArray(gptValue) || gptValue.length === 0) return stateValues;

    const validatedAI: IChemicalPhraseFill[] = [];
    for (const item of gptValue) {
      if (!item || typeof item !== 'object') continue;
      const { code, slotIndex, value } = item as { code?: unknown; slotIndex?: unknown; value?: unknown };
      if (typeof code !== 'string' || !activeCodes?.includes(code)) continue;
      if (typeof slotIndex !== 'number' || !Number.isInteger(slotIndex)) continue;
      if (typeof value !== 'string' || isBlank(value)) continue;
      const codeSpec = spec[code];
      if (!codeSpec) continue;
      if (slotIndex < 0 || slotIndex >= codeSpec.slots.length) continue;
      // A leading space can separate a completion from the fixed phrase text.
      validatedAI.push({ code, slotIndex, value });
    }
    if (validatedAI.length === 0) return stateValues;

    const slotKey = (f: { code: string; slotIndex: number }) => `${f.code}:${f.slotIndex}`;
    const occupied = new Set((stateValues ?? []).map(slotKey));
    const additions = validatedAI.filter(f => {
      const key = slotKey(f);
      if (occupied.has(key)) return false;
      occupied.add(key);
      return true;
    });
    if (additions.length === 0) return stateValues;
    modifiedCount++;
    return [...(stateValues ?? []), ...additions];
  };

  const overWriteLabels = (
    state: IProduct,
    rootCategory?: IFoodlaCategory,
    gptLabels?: Record<string, boolean> | Array<{ field: string; state: boolean }>
  ): ILabel[] | undefined => {
    const stateLabels = state.labels;

    if (!gptLabels || (typeof gptLabels === 'object' && Object.keys(gptLabels).length === 0)) {
      return stateLabels;
    }

    // Get all available label fields from the constants
    const allLabelFields = getAllLabelsByCategrory(state.foodlaCategory, rootCategory).map(label => label.field);

    // Prepare new labels array
    let newLabels: ILabel[] = stateLabels ? [...stateLabels] : [];
    let labelsModified = false;

    // Handle both array format and object format from GPT
    if (Array.isArray(gptLabels)) {
      // Handle array format: [{ field: "ecological", state: true }, ...]
      gptLabels.forEach(labelItem => {
        const { field, state } = labelItem;

        // Only process if it's a valid label field
        if (allLabelFields.includes(field)) {
          // Find existing label
          const existingLabelIndex = newLabels.findIndex(label => label.field === field);

          if (existingLabelIndex >= 0) {
            // Update existing label if the value is different
            if (newLabels[existingLabelIndex].state !== state) {
              newLabels[existingLabelIndex] = { ...newLabels[existingLabelIndex], state };
              labelsModified = true;
            }
          } else if (state) {
            // Add new label if it doesn't exist and value is true
            newLabels.push({ field, state });
            labelsModified = true;
          }
        }
      });
    } else {
      // Handle object format: { ecological: true, lactoseFree: true, ... }
      Object.entries(gptLabels).forEach(([field, state]) => {
        // Only process if it's a valid label field
        if (allLabelFields.includes(field)) {
          // Find existing label
          const existingLabelIndex = newLabels.findIndex(label => label.field === field);

          if (existingLabelIndex >= 0) {
            // Update existing label if the value is different
            if (newLabels[existingLabelIndex].state !== state) {
              newLabels[existingLabelIndex] = { ...newLabels[existingLabelIndex], state };
              labelsModified = true;
            }
          } else if (state) {
            // Add new label if it doesn't exist and value is true
            newLabels.push({ field, state });
            labelsModified = true;
          }
        }
      });
    }

    if (labelsModified) {
      modifiedCount++;
      return newLabels;
    }

    return stateLabels;
  };

  // other_substances: supplement substance rows {name, amount, unit}. Mirrors the
  // "never clobber user input" contract — fill only when the user has no rows yet.
  // Sanitises like the save path (ProductUpdateForm/utils.js): trim, coerce amount
  // → string, drop rows without a name (name is the row identifier). Returns undefined
  // (never []) when nothing should change, so removeNilProperty preserves existing state.
  const overWriteOtherSubstances = (
    stateValue?: IProduct['other_substances'],
    gptValue?: any
  ): IProduct['other_substances'] | undefined => {
    if (Array.isArray(stateValue) && stateValue.length > 0) return undefined;
    if (!Array.isArray(gptValue)) return undefined;
    // Only accept units that exist in the editor's dropdown (SUPPLEMENT_SUBSTANCE_UNITS). The AI may
    // read a label unit verbatim, but an out-of-list value isn't selectable in the fixed dropdown —
    // blank it (keep the row's name + amount) so the user picks a valid unit.
    const validUnits = new Set(SUPPLEMENT_SUBSTANCE_UNITS.map(u => u.value));
    const rows = gptValue
      .map((s: any) => {
        const rawUnit = typeof s?.unit === 'string' ? s.unit.trim() : '';
        return {
          name: typeof s?.name === 'string' ? s.name.trim() : '',
          amount: typeof s?.amount === 'number' ? s.amount : typeof s?.amount === 'string' && s.amount.trim() ? parseFloat(s.amount.replace(',', '.')) || undefined : undefined,
          unit: validUnits.has(rawUnit) ? rawUnit : '',
        };
      })
      .filter((s: { name: string }) => s.name);
    if (rows.length === 0) return undefined;
    modifiedCount++;
    return rows;
  };

  // dagsdosUnit: per-dagsdos basis unit (DAGSDOS_UNITS_MAP). Fill only when blank, and only
  // if the AI value is a selectable dropdown option — an out-of-list unit isn't pickable, so
  // blank it (the paired dagsdosAmount can still fill via overWriteNumberValue). Returns
  // undefined when nothing should change so removeNilProperty preserves existing state.
  const overWriteDagsdosUnit = (stateValue?: string, gptValue?: any): string | undefined => {
    if (!isBlank(stateValue)) return undefined;
    const raw = typeof gptValue === 'string' ? gptValue.trim() : '';
    if (!raw) return undefined;
    const validUnits = new Set(DAGSDOS_UNITS_MAP.map(u => u.value));
    if (!validUnits.has(raw)) return undefined;
    modifiedCount++;
    return raw;
  };

  const isNonFood = isNewNonFood(state?.foodlaCategory, rootCategory);

  console.log(`fett: ${state.fett}, ${data.fett}, ${overWriteNumberValue(state.fett, data.fett)}`);
  console.log(
    `kolhydrat: ${state.kolhydrat}, ${data.kolhydrat}, ${overWriteNumberValue(state.kolhydrat, data.kolhydrat)}`
  );
  let updatingState: Partial<IProduct> = {
    title: overWriteStringValue(state.title, data.title),
    EAN: overWriteStringValue(state.EAN, data.EAN),
    short_text: overWriteStringValue(state.short_text, data.short_text),
    ingredient_statement: overWriteStringValue(state.ingredient_statement, data.ingredient_statement),
    non_food_ingredients: overWriteStringValue(state.non_food_ingredients, data.non_food_ingredients),
    animalFoodIngredients: overWriteStringValue(state.animalFoodIngredients, data.animalFoodIngredients),
    feedAdditives: overWriteStringValue(state.feedAdditives, data.feedAdditives),
    feedingInstructions: overWriteStringValue(state.feedingInstructions, data.feedingInstructions),
    analyticalConstituentsFeed: overWriteStringValue(state.analyticalConstituentsFeed, data.analyticalConstituentsFeed),
    trade_item_temperature_information: overWriteStringValue(
      state.trade_item_temperature_information,
      data.trade_item_temperature_information
    ),
    energi_kj: overWriteNumberValue(state.energi_kj, data.energi_kj),
    energi_kcal: overWriteNumberValue(state.energi_kcal, data.energi_kcal),
    fett: overWriteNumberValue(state.fett, data.fett),
    mattatFett: overWriteNumberValue(state.mattatFett, data.mattatFett),
    enkelomattatFett: overWriteNumberValue(state.enkelomattatFett, data.enkelomattatFett),
    flerromattatFett: overWriteNumberValue(state.flerromattatFett, data.flerromattatFett),
    polyoler: overWriteNumberValue(state.polyoler, data.polyoler),
    starkelse: overWriteNumberValue(state.starkelse, data.starkelse),
    kolhydrat: overWriteNumberValue(state.kolhydrat, data.kolhydrat),
    sockerarter: overWriteNumberValue(state.sockerarter, data.sockerarter),
    fiber: overWriteNumberValue(state.fiber, data.fiber),
    protein: overWriteNumberValue(state.protein, data.protein),
    salt: overWriteNumberValue(state.salt, data.salt),
    descriptive_size_amount: overWriteNumberValue(state.descriptive_size_amount, data.descriptive_size_amount, {
      checkZero: true,
    }),
    descriptive_size_unit: overWriteStringValue(state.descriptive_size_unit, data.descriptive_size_unit),
    min_temperature: overWriteNumberValue(state.min_temperature, data.minTemperature),
    max_temperature: overWriteNumberValue(state.max_temperature, data.maxTemperature),
    gross_weight_num: overWriteNumberValue(state.gross_weight_num, data.gross_weight_num),
    place_of_item_activity: overWriteStringValue(state.place_of_item_activity, data.place_of_item_activity),
    classification: overWriteStringValue(state.classification, data.classification),

    search_terms: mergeSearchTerms(state.search_terms, data.search_terms),
    fishProductionMethod: overWriteEnumValue(
      state.fishProductionMethod,
      data.fishProductionMethod,
      FishProductionMethodEnum
    ),
    fishCatchMethod: overWriteEnumValue(state.fishCatchMethod, data.fishCatchMethod, FishCatchMethodEnum),
    catchArea: overWriteEnumValue(state.catchArea, data.catchArea, CatchAreaEnum),
    eggWeightClass: overWriteEnumValue(state.eggWeightClass, data.eggWeightClass, EggWeightClassEnum),
    eggPackerApprovalNumber: overWriteStringValue(state.eggPackerApprovalNumber, data.eggPackerApprovalNumber),
    alcoholPercentage: overWriteNumberValue(state.alcoholPercentage, data.alcoholPercentage, { checkZero: true }),
    recycling_amount: overWriteNumberValue(state.recycling_amount, data.recycling_amount),
    preparationInstructions: overWriteStringValue(state.preparationInstructions, data.preparationInstructions),
    responsibleLabelingApprovalNumber: overWriteStringValue(
      state.responsibleLabelingApprovalNumber,
      data.responsibleLabelingApprovalNumber
    ),
    animalCategoriesForFeed: overWriteEnumListValue(
      state.animalCategoriesForFeed,
      data.animalCategoriesForFeed,
      AnimalCategoriesForFeedEnum
    ),
    animalFeedType: overWriteEnumValue(state.animalFeedType, data.animalFeedType, AnimalFeedTypeEnum),
    animalAgeGroup: overWriteStringValue(state.animalAgeGroup, data.animalAgeGroup),
    sunProtectionFactor: overWriteEnumValue(
      state.sunProtectionFactor,
      data.sunProtectionFactor,
      SunProtectionFactorEnum
    ),
    categoryWeightloss: overWriteEnumValue(state.categoryWeightloss, data.categoryWeightloss, CategoryWeightlossEnum),
    ceMarking: overWriteBooleanValue(state.ceMarking, data.ceMarking),

    zink: overWriteNumberValue(state.zink, data.zink),
    vitaminK: overWriteNumberValue(state.vitaminK, data.vitaminK),
    vitaminE: overWriteNumberValue(state.vitaminE, data.vitaminE),
    vitaminD: overWriteNumberValue(state.vitaminD, data.vitaminD),
    vitaminC: overWriteNumberValue(state.vitaminC, data.vitaminC),
    vitaminB6: overWriteNumberValue(state.vitaminB6, data.vitaminB6),
    vitaminB12: overWriteNumberValue(state.vitaminB12, data.vitaminB12),
    vitaminA: overWriteNumberValue(state.vitaminA, data.vitaminA),
    tiamin: overWriteNumberValue(state.tiamin, data.tiamin),
    selen: overWriteNumberValue(state.selen, data.selen),
    riboflavin: overWriteNumberValue(state.riboflavin, data.riboflavin),
    pantotensyra: overWriteNumberValue(state.pantotensyra, data.pantotensyra),
    niacin: overWriteNumberValue(state.niacin, data.niacin),
    molybden: overWriteNumberValue(state.molybden, data.molybden),
    mangan: overWriteNumberValue(state.mangan, data.mangan),
    magnesium: overWriteNumberValue(state.magnesium, data.magnesium),
    krom: overWriteNumberValue(state.krom, data.krom),
    koppar: overWriteNumberValue(state.koppar, data.koppar),
    klorid: overWriteNumberValue(state.klorid, data.klorid),
    kalium: overWriteNumberValue(state.kalium, data.kalium),
    kalcium: overWriteNumberValue(state.kalcium, data.kalcium),
    jod: overWriteNumberValue(state.jod, data.jod),
    jarn: overWriteNumberValue(state.jarn, data.jarn),
    fosfor: overWriteNumberValue(state.fosfor, data.fosfor),
    folsyra: overWriteNumberValue(state.folsyra, data.folsyra),
    fluorid: overWriteNumberValue(state.fluorid, data.fluorid),
    biotin: overWriteNumberValue(state.biotin, data.biotin),
    // Add the labels field
    labels: overWriteLabels(state, rootCategory, data.labels),
    gross_height: overWriteNumberValue(state.gross_height, data.gross_height),
    gross_width: overWriteNumberValue(state.gross_width, data.gross_width),
    gross_depth: overWriteNumberValue(state.gross_depth, data.gross_depth),
    consumerContactCompanyName: overWriteStringValue(
      state.consumerContactCompanyName,
      data.consumer_contact_company_name
    ),
    consumerContactPhone: overWriteStringValue(state.consumerContactPhone, data.consumer_contact_phone),
    consumerContactEmail: overWriteStringValue(state.consumerContactEmail, data.consumer_contact_email),
    consumerContactWebsite: overWriteStringValue(state.consumerContactWebsite, data.consumer_contact_website),
    consumerContactAddress: overWriteStringValue(state.consumerContactAddress, data.consumer_contact_address),
    wasteManagementAndRecyclingInformation: overWriteStringValue(
      state.wasteManagementAndRecyclingInformation,
      data.wasteManagementAndRecyclingInformation
    ),
  };

  if (
    (isNewFood(state?.foodlaCategory, rootCategory) ||
    isToy(state?.foodlaCategory, rootCategory) ||
    isElectric(state?.foodlaCategory, rootCategory) ||
    isJewelry(state?.foodlaCategory, rootCategory) ||
    isGoods(state?.foodlaCategory, rootCategory) ||
    isChemical(state?.foodlaCategory, rootCategory) ||
    isDetergent(state?.foodlaCategory, rootCategory) ||
    isCosmetic(state?.foodlaCategory, rootCategory) ||
    isSunscreen(state?.foodlaCategory, rootCategory) ||
    isOTC(state?.foodlaCategory, rootCategory)) && isNotStore
  ) {
    updatingState = {
      ...updatingState,
      childrenUnder16: overWriteBooleanValue(state.childrenUnder16, data.childrenUnder16),
    };
  }

  if (isMedical(state?.foodlaCategory, rootCategory)) {
    updatingState = {
      ...updatingState,
      checkUniqueIdentification: overWriteBooleanValue(state.checkUniqueIdentification, data.checkUniqueIdentification),
    };
    if (!updatingState.checkUniqueIdentification) {
      updatingState = {
        ...updatingState,
        explanationNoUDI: overWriteStringValue(state.explanationNoUDI, data.explanationNoUDI),
      };
    }
  }
  if (
    (isGoods(state?.foodlaCategory, rootCategory) ||
    isJewelry(state?.foodlaCategory, rootCategory) ||
    isToy(state?.foodlaCategory, rootCategory) ||
    isElectric(state?.foodlaCategory, rootCategory) ||
    isMedical(state?.foodlaCategory, rootCategory)) && updatingState.ceMarking
  ) {
    updatingState = {
      ...updatingState,
      specifyCEStandards: overWriteStringValue(state.specifyCEStandards, data.specifyCEStandards),
    };
  }
  if (isFeedCategory(state?.foodlaCategory, rootCategory)) {
    updatingState = {
      ...updatingState,
      approvalDietFeed: overWriteEnumValue(state.approvalDietFeed, data.approvalDietFeed, ApprovalDietFeedEnum),
    };
  }
    

  if (isElectricBattery(state?.foodlaCategory, rootCategory)) {
    updatingState = {
      ...updatingState,
      batteryModel: overWriteStringValue(state.batteryModel, data.batteryModel),
      batteryType: overWriteEnumValue(state.batteryType, data.batteryType, BatteryTypeEnum),
      batteryTypeOther: overWriteStringValue(state.batteryTypeOther, data.batteryTypeOther),
      batteryVoltage: overWriteNumberValue(state.batteryVoltage, data.batteryVoltage),
      batteryTechnology: overWriteEnumValue(
        state.batteryTechnology,
        data.batteryTechnology,
        BatteryTechnologyEnum,
      ),
      batteryCapacity: overWriteNumberValue(state.batteryCapacity, data.batteryCapacity),
      batteryLife: overWriteNumberValue(state.batteryLife, data.batteryLife),
      batteryWeight: overWriteNumberValue(state.batteryWeight, data.batteryWeight),
      batteryRechargeable: overWriteBooleanValue(state.batteryRechargeable, data.batteryRechargeable),
      manufacturingLocation: overWriteStringValue(state.manufacturingLocation, data.manufacturingLocation),
      batteryHazardousMetals: overWriteEnumListValue(
        state.batteryHazardousMetals,
        data.batteryHazardousMetals,
        BatteryHazardousMetalEnum,
      ),
    };
  }

  // islamp
  if (isElectricLamp(state?.foodlaCategory, rootCategory)) {
    updatingState = {
      ...updatingState,
      lampEffect: overWriteNumberValue(state.lampEffect, data.lampEffect),
      lampModel: overWriteStringValue(state.lampModel, data.lampModel),
      lampEnergyClass: overWriteEnumValue(state.lampEnergyClass, data.lampEnergyClass, LampEnergyClassEnum),
      lampTechnology: overWriteStringValue(state.lampTechnology, data.lampTechnology),
      lampSocketType: overWriteStringValue(state.lampSocketType, data.lampSocketType),
      lampDimmable: overWriteBooleanValue(state.lampDimmable, data.lampDimmable),
      lampEquivalentBulb: overWriteStringValue(state.lampEquivalentBulb, data.lampEquivalentBulb),
      lampBrightnessLm: overWriteNumberValue(state.lampBrightnessLm, data.lampBrightnessLm),
      lampColorTemperatureK: overWriteNumberValue(state.lampColorTemperatureK, data.lampColorTemperatureK),
      lampAverageLifespanH: overWriteNumberValue(state.lampAverageLifespanH, data.lampAverageLifespanH),
      lampColorRenderingRa: overWriteNumberValue(state.lampColorRenderingRa, data.lampColorRenderingRa),
      lampEnergyConsumption: overWriteNumberValue(state.lampEnergyConsumption, data.lampEnergyConsumption),
      lampBeamAngleDeg: overWriteNumberValue(state.lampBeamAngleDeg, data.lampBeamAngleDeg),
    };
  }

  if (
    (isNonFood ||
      isSpecialFoodNutrition(state?.foodlaCategory, rootCategory) ||
      isFoodSupplements(state?.foodlaCategory, rootCategory)) &&
    !isFeedCategory(state?.foodlaCategory, rootCategory)
  ) {
    updatingState = {
      ...updatingState,
      dosageAndUsageInstructions: overWriteStringValue(
        state.dosageAndUsageInstructions,
        data.dosageAndUsageInstructions
      ),
    };
  }

  // Supplement substances (kosttillskott) — same category gate as the OtherSubstances
  // form section (ProductUpdateForm/index.js); AI fills only when that section is shown.
  if (
    isSpecialFoodNutrition(state?.foodlaCategory, rootCategory) ||
    isFoodSupplements(state?.foodlaCategory, rootCategory)
  ) {
    updatingState = {
      ...updatingState,
      other_substances: overWriteOtherSubstances(state.other_substances, data.other_substances),
      // dagsdos basis (amount + unit) — mirrors the descriptive_size_amount/unit pattern. Only
      // meaningful when nutritional_unit = DAGSDOS; on a non-DAGSDOS basis the save path clears
      // both, and an out-of-DAGSDOS basis hides them in the form.
      dagsdosAmount: overWriteNumberValue(state.dagsdosAmount, data.dagsdosAmount, { checkZero: true }),
      dagsdosUnit: overWriteDagsdosUnit(state.dagsdosUnit, data.dagsdosUnit),
    };
  }

  if ((isSuperuser && isApoteaProductProducer) || isApoteaProducer) {
    updatingState = {
      ...updatingState,
      shortSalesDescription: overWriteStringValue(state.shortSalesDescription, data.shortSalesDescription),
    };
  }

  if (isBeverages(state?.foodlaCategory, rootCategory)) {
    updatingState = {
      ...updatingState,
      recyclingType: overWriteEnumValue(state.recyclingType, data.recyclingType, RecyclingTypeEnum),
    };
  }

  if (
    isPharma(state?.foodlaCategory, rootCategory) ||
    isDetergent(state?.foodlaCategory, rootCategory) ||
    isComplementarySupplements(state?.foodlaCategory, rootCategory) ||
    isSpecialFoodNutrition(state?.foodlaCategory, rootCategory) ||
    isFoodSupplements(state?.foodlaCategory, rootCategory)
  ) {
    updatingState = {
      ...updatingState,
      preparedForm: overWriteEnumValue(state.preparedForm, data.preparedForm, PreparedFormEnum),
    };
  }

  if (isNonFood) {
    updatingState = {
      ...updatingState,
      additionalCharacteristics: overWriteEnumListValue(
        state.additionalCharacteristics,
        data.additionalCharacteristics,
        AdditionalCharacteristicsEnum
      ),
    };
  }

  if (isWhiteSnus(state.foodlaCategory, rootCategory)) {
    updatingState = {
      ...updatingState,
      whiteSnusNicotinePerPortion: overWriteNumberValue(
        state.whiteSnusNicotinePerPortion,
        data.whiteSnusNicotinePerPortion
      ),
      whiteSnusNicotinePerGram: overWriteNumberValue(state.whiteSnusNicotinePerGram, data.whiteSnusNicotinePerGram),
      whiteSnusNicotineStrength: overWriteStringValue(state.whiteSnusNicotineStrength, data.whiteSnusNicotineStrength),
      whiteSnusFlavor: overWriteStringValue(state.whiteSnusFlavor, data.whiteSnusFlavor),
      whiteSnusPortionsPerUnit: overWriteNumberValue(state.whiteSnusPortionsPerUnit, data.whiteSnusPortionsPerUnit),
      whiteSnusWeightPerPortion: overWriteNumberValue(state.whiteSnusWeightPerPortion, data.whiteSnusWeightPerPortion),
      whiteSnusFormat: overWriteStringValue(state.whiteSnusFormat, data.whiteSnusFormat),
    };
  }

  if (
    isNonFood ||
    isFish(state?.foodlaCategory, rootCategory, state) ||
    isSpecialFoodNutrition(state?.foodlaCategory, rootCategory) ||
    isBeverages(state?.foodlaCategory, rootCategory) ||
    isFoodSupplements(state?.foodlaCategory, rootCategory) ||
    isFruktOrGreen(state?.foodlaCategory, rootCategory) ||
    isOtherFoodstuffs(state?.foodlaCategory, rootCategory) ||
    isMeat(state?.foodlaCategory, rootCategory) ||
    isBakeryProducts(state?.foodlaCategory, rootCategory) ||
    isMilk(state?.foodlaCategory, rootCategory) ||
    isCereals(state?.foodlaCategory, rootCategory) ||
    isConfectionery(state?.foodlaCategory, rootCategory) ||
    isConsumptionReadySnacks(state?.foodlaCategory, rootCategory) ||
    isSweet(state?.foodlaCategory, rootCategory) ||
    isFats(state?.foodlaCategory, rootCategory) ||
    isSpicesSoupsSalads(state?.foodlaCategory, rootCategory) ||
    isIceCream(state?.foodlaCategory, rootCategory)
  ) {
    updatingState = {
      ...updatingState,
      country_of_manufacturing: overWriteNumberValue(state.country_of_manufacturing, data.country_of_manufacturing),
    };
  }

  if (
    !isWhiteSnus(state?.foodlaCategory, rootCategory) &&
    (isGoods(state?.foodlaCategory, rootCategory) ||
      isJewelry(state?.foodlaCategory, rootCategory) ||
      isToy(state?.foodlaCategory, rootCategory) ||
      isElectric(state?.foodlaCategory, rootCategory))
  ) {
    updatingState = {
      ...updatingState,
      color: overWriteStringValue(state.color, data.color),
    };
  }

  if (
    !isWhiteSnus(state.foodlaCategory, rootCategory) &&
    (isGoods(state.foodlaCategory, rootCategory) ||
      isJewelry(state.foodlaCategory, rootCategory) ||
      isToy(state.foodlaCategory, rootCategory) ||
      isElectric(state.foodlaCategory, rootCategory) ||
      isMedical(state.foodlaCategory, rootCategory))
  ) {
    updatingState = {
      ...updatingState,
      material: overWriteStringValue(state.material, data.material),
      dimensions: overWriteStringValue(state.dimensions, data.dimensions),
    };
  }

  if (isSewingKnittingYarn(state.foodlaCategory, rootCategory)) {
    updatingState = {
      ...updatingState,
      yarnWeight: overWriteNumberValue(state.yarnWeight, data.yarnWeight),
      yarnRunLength: overWriteNumberValue(state.yarnRunLength, data.yarnRunLength),
      recommendedCrochetHook: overWriteStringValue(state.recommendedCrochetHook, data.recommendedCrochetHook),
      recommendedKnittingNeedles: overWriteStringValue(
        state.recommendedKnittingNeedles,
        data.recommendedKnittingNeedles
      ),
      knittingGaugeStitches: overWriteNumberValue(state.knittingGaugeStitches, data.knittingGaugeStitches),
      knittingGaugeRows: overWriteNumberValue(state.knittingGaugeRows, data.knittingGaugeRows),
      crochetGaugeStitches: overWriteNumberValue(state.crochetGaugeStitches, data.crochetGaugeStitches),
      crochetGaugeRows: overWriteNumberValue(state.crochetGaugeRows, data.crochetGaugeRows),
      washingTemperature: overWriteNumberValue(state.washingTemperature, data.washingTemperature),
      washingInstructions: overWriteStringValue(state.washingInstructions, data.washingInstructions),
      ironing: overWriteEnumValue(state.ironing, data.ironing, IroningEnum),
      tumbleDrying: overWriteBooleanValue(state.tumbleDrying, data.tumbleDrying),
      dryCleaning: overWriteBooleanValue(state.dryCleaning, data.dryCleaning),
      chlorineBleaching: overWriteBooleanValue(state.chlorineBleaching, data.chlorineBleaching),
    };
  }

  if (!isBlank(updatingState.title)) {
    updatingState.title = (updatingState.title || '')
      .split(' ')
      .map(text => (text[0] || '').toUpperCase() + (text.slice(1) || '').toLowerCase())
      .join(' ');
  }

  // nutritional_unit (näringsvärde-basis) is seeded to GRAM by getInitialState, so a
  // plain blank-only guard would never fire and the AI could never set the basis (incl.
  // DAGSDOS for per-dose supplements). Treat the seeded GRAM as overridable — same pattern
  // as brand_food's producer-name default: let the AI set the basis when state is still
  // blank or the GRAM default, but never clobber an explicit MILLILITER/DAGSDOS the user
  // already chose. (If the AI picks DAGSDOS on a non-supplement, the form's
  // selectedNutritionalUnit/save coercion forces it back to GRAM, so no category gate needed.)
  if (
    !isBlank(data.nutritional_unit) &&
    [NutritionalUnit.GRAM, NutritionalUnit.MILLILITER, NutritionalUnit.DAGSDOS].includes(data.nutritional_unit) &&
    (isBlank(state.nutritional_unit) || (isCreatingNew && state.nutritional_unit === NutritionalUnit.GRAM))
  ) {
    if (state.nutritional_unit !== data.nutritional_unit) modifiedCount++;
    updatingState.nutritional_unit = data.nutritional_unit;
  }

  if (isFood) {
    if (isBlank(state.brand_food)) {
      updatingState.brand_food = data.brand_food || data.brand;
    } else if (isCreatingNew && getProducerName(undefined, producer) === state.brand_food) {
      updatingState.brand_food = data.brand_food || data.brand;
    }
  } else {
    if (isBlank(state.brand)) {
      updatingState.brand = data.brand || data.brand_food;
    } else if (isCreatingNew && getProducerName(undefined, producer) === state.brand) {
      updatingState.brand = data.brand || data.brand_food;
    }
  }

  if (!isBlank(data.signalWords)) {
    const key = data.signalWords!;
    if (key in SignalWordsEnum) {
      // const signalWords = SignalWordsEnum[key as keyof typeof SignalWordsEnum];
      // alert(`signalWords: ${signalWords}`);
      updatingState.signalWords = key;
    }
  }
  if (!isNil(data.hazardStatements) && Array.isArray(data.hazardStatements)) {
    const value: string[] = [];
    data.hazardStatements.forEach((item: string) => {
      if (!isBlank(item)) {
        if (item in HazardStatementsEnum) {
          value.push(item);
        } else {
          console.error(`Invalid hazard statement: ${item}`);
        }
      }
    });
    if (value.length > 0) {
      updatingState.hazardStatements = value as any[];
    }
  }

  if (!isNil(data.securityNotices) && Array.isArray(data.securityNotices)) {
    const value: string[] = [];
    data.securityNotices.forEach((item: string) => {
      if (!isBlank(item)) {
        if (item in SecurityNoticesEnum) {
          value.push(item);
        } else {
          console.error(`Invalid security notices: ${item}`);
        }
      }
    });
    if (value.length > 0) {
      updatingState.securityNotices = value as any[];
    }
  }

  if (!isNil(data.hazardSymbols) && Array.isArray(data.hazardSymbols)) {
    const value: string[] = [];
    data.hazardSymbols.forEach((item: string) => {
      if (!isBlank(item)) {
        if (item in HazardSymbolsEnum) {
          value.push(item);
        } else {
          console.error(`Invalid hazard symbols: ${item}`);
        }
      }
    });
    if (value.length > 0) {
      updatingState.hazardSymbols = value as any[];
    }
  }

  // flammableSubtype is only meaningful when FLAMMABLE is in the (current or
  // newly-set) hazardSymbols array. Mirror the UI's auto-clear in
  // hazard-fields.tsx — if AI's overwrite removed FLAMMABLE, also clear any
  // existing flammableSubtype so it doesn't linger as orphan data.
  const effectiveHazardSymbols = (updatingState.hazardSymbols as string[] | undefined) ?? state.hazardSymbols ?? [];
  const hasFlammable = effectiveHazardSymbols.includes('FLAMMABLE' as HazardSymbolsEnum);
  if (hasFlammable) {
    const next = overWriteEnumValue(state.flammableSubtype ?? undefined, data.flammableSubtype, FlammableSubtypeEnum);
    if (next !== undefined) {
      updatingState.flammableSubtype = next;
    }
  } else if (state.flammableSubtype) {
    updatingState.flammableSubtype = null;
  }

  // hazardStatements / securityNotices are always overwritten by AI when valid
  // (existing behaviour). When that happens, drop any pre-existing fills whose
  // parent code is no longer in the active code array — otherwise stale fills
  // persist bound to codes that don't exist anymore.
  const newHazardStatements = updatingState.hazardStatements as string[] | undefined;
  const cleanedHazardFills = newHazardStatements
    ? dropOrphanFills(state.hazardStatementFills, newHazardStatements)
    : state.hazardStatementFills;
  updatingState.hazardStatementFills = overWriteSlotFills(
    cleanedHazardFills,
    data.hazardStatementFills,
    HAZARD_STATEMENT_PLACEHOLDERS,
    newHazardStatements ?? state.hazardStatements
  );

  const newSecurityNotices = updatingState.securityNotices as string[] | undefined;
  const cleanedSecurityFills = newSecurityNotices
    ? dropOrphanFills(state.securityNoticeFills, newSecurityNotices)
    : state.securityNoticeFills;
  updatingState.securityNoticeFills = overWriteSlotFills(
    cleanedSecurityFills,
    data.securityNoticeFills,
    SECURITY_NOTICE_PLACEHOLDERS,
    newSecurityNotices ?? state.securityNotices
  );

  // Logistical info — only filled when AI explicitly extracts from a logistics
  // image (carton label, supplier spec, pallet sheet). Default helpers respect
  // the "never overwrite existing" contract. (gross_weight_num + gross_height/
  // width/depth are already in the initial updatingState block above.)
  updatingState = {
    ...updatingState,
    // Distributionsförpackning / store pack
    storePackEANForStorePack: overWriteStringValue(state.storePackEANForStorePack, data.storePackEANForStorePack),
    storePackTradeItemMeasurementsLengthDepth: overWriteNumberValue(
      state.storePackTradeItemMeasurementsLengthDepth,
      data.storePackTradeItemMeasurementsLengthDepth
    ),
    storePackTradeItemMeasurementsWidth: overWriteNumberValue(
      state.storePackTradeItemMeasurementsWidth,
      data.storePackTradeItemMeasurementsWidth
    ),
    storePackTradeItemMeasurementsHeight: overWriteNumberValue(
      state.storePackTradeItemMeasurementsHeight,
      data.storePackTradeItemMeasurementsHeight
    ),
    storePackGrossWeight: overWriteNumberValue(state.storePackGrossWeight, data.storePackGrossWeight),
    // Transportförpackning
    transportEANForStorePack: overWriteStringValue(state.transportEANForStorePack, data.transportEANForStorePack),
    transportQtyOfBaseItem: overWriteNumberValue(state.transportQtyOfBaseItem, data.transportQtyOfBaseItem),
    transportQtyOfNextLowerItem: overWriteNumberValue(
      state.transportQtyOfNextLowerItem,
      data.transportQtyOfNextLowerItem
    ),
    transportTradeItemMeasurementsLengthDepth: overWriteNumberValue(
      state.transportTradeItemMeasurementsLengthDepth,
      data.transportTradeItemMeasurementsLengthDepth
    ),
    transportTradeItemMeasurementsWidth: overWriteNumberValue(
      state.transportTradeItemMeasurementsWidth,
      data.transportTradeItemMeasurementsWidth
    ),
    transportTradeItemMeasurementsHeight: overWriteNumberValue(
      state.transportTradeItemMeasurementsHeight,
      data.transportTradeItemMeasurementsHeight
    ),
    transportGrossWeight: overWriteNumberValue(state.transportGrossWeight, data.transportGrossWeight),
    // Pall
    palletEANForStorePack: overWriteStringValue(state.palletEANForStorePack, data.palletEANForStorePack),
    palletQtyOfBaseItem: overWriteNumberValue(state.palletQtyOfBaseItem, data.palletQtyOfBaseItem),
    palletQtyOfStorePack: overWriteNumberValue(state.palletQtyOfStorePack, data.palletQtyOfStorePack),
    palletQtyOfNextLowerItem: overWriteNumberValue(state.palletQtyOfNextLowerItem, data.palletQtyOfNextLowerItem),
    palletTradeItemMeasurementsLengthDepth: overWriteNumberValue(
      state.palletTradeItemMeasurementsLengthDepth,
      data.palletTradeItemMeasurementsLengthDepth
    ),
    palletTradeItemMeasurementsWidth: overWriteNumberValue(
      state.palletTradeItemMeasurementsWidth,
      data.palletTradeItemMeasurementsWidth
    ),
    palletTradeItemMeasurementsHeight: overWriteNumberValue(
      state.palletTradeItemMeasurementsHeight,
      data.palletTradeItemMeasurementsHeight
    ),
    palletGrossWeight: overWriteNumberValue(state.palletGrossWeight, data.palletGrossWeight),
    // LAV
    lavQuantity: overWriteNumberValue(state.lavQuantity, data.lavQuantity),
  };

  if (isNotStore) {
    updatingState.purchasingDataSupplierArticleNo = overWriteStringValue(
      state.purchasingDataSupplierArticleNo,
      typeof data.purchasingDataSupplierArticleNo === 'string'
        ? data.purchasingDataSupplierArticleNo.trim()
        : undefined
    );
  }

  // B-packstorlek (kfp/dfp) seeds `1` as a math-don't-break default on new
  // products, so the standard "fill if blank" helper can never override it.
  // When the AI extracts a real value from a logistics image, let it win —
  // same precedent as signalWords / hazardSymbols above. Applies to all
  // producers so we collect data regardless of organisation.
  if (!isNil(data.consumer_size_to_order_size_ratio)) {
    if (state.consumer_size_to_order_size_ratio !== data.consumer_size_to_order_size_ratio) {
      modifiedCount++;
    }
    updatingState.consumer_size_to_order_size_ratio = data.consumer_size_to_order_size_ratio;
  }

  return { modifiedCount, data: removeNilProperty(updatingState) };
};

interface ChatGPTProps {
  isNew: boolean;
  isFood?: boolean;
  visionTab: string;
  image?: IImage;
  imageData?: IImage;
  info?: {
    chatGPT?: Record<string, string>;
  };
  state: IProduct;
  setState: (state: IProduct) => void;
}

const ChatGPT: FC<ChatGPTProps> = ({ visionTab, isNew, isFood, image, imageData, info, state, setState }) => {
  const keycloakCtx = useContext(KeycloakContext);
  const token = path(['keycloak', 'token'], keycloakCtx);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Record<string, any>>({});
  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>({});

  const handleSendImageToChatGpt = async () => {
    if (!imageData) return;

    const formData = new FormData();

    try {
      if (isNew) {
        const imageFile = await fetch(imageData.imgSrc || '').then(response => response.blob());
        formData.append('files', imageFile);
      } else {
        formData.append('imageUrl', imageData.pictureUrl || '');
      }

      // For the Magic Upload run log: where the request came from.
      formData.append('source', 'vision');
      setLoading(true);
      const { data } = await axios.post<{ data: Record<string, any> }>(restEndpoints.aiImage, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('response', data.data);
      setData(removeNilProperty(data.data));
      console.log('chatGPT', data);
      return data;
    } catch (error: any) {
      return { error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const handleFillData = () => {
    let { data: updatingState } = mapChatGptDataToFill(state, data, { isFood });

    Object.entries(selectedFields).forEach(([field, checked]) => {
      if (!checked) {
        delete updatingState[field as keyof IProduct];
      }
    });

    updatingState = removeNilProperty(updatingState);

    setState({ ...state, ...updatingState });
  };

  useEffect(() => {
    setData(removeNilProperty(info?.chatGPT));
  }, [info]);

  useEffect(() => {
    const newSelectedData: Record<string, boolean> = {};
    Object.keys(data).forEach(field => {
      newSelectedData[field] = true;
    });
    setSelectedFields(newSelectedData);
  }, [data]);

  useEffect(() => {
    console.log('ChatGPT', data);
  }, [data]);

  return (
    <VisionTab
      index="chatGPT"
      tabValue={visionTab}
      image={image}
      pictureWidth={6}
      info={info}
      isNew={isNew}
      hideImage={false}
      vertices={undefined}
      showLabels={undefined}
      normalizedVertices={undefined}
    >
      {!!imageData && (
        <Button disabled={loading} color="primary" variant="contained" size="small" onClick={handleSendImageToChatGpt}>
          {loading ? (
            <CircularProgress size={24} style={{ display: 'block' }} />
          ) : isEmpty(data) ? (
            <img src={ChatGptLogo} alt="chat-gpt-logo" style={{ width: 24 }} />
          ) : (
            <RetryIcon />
          )}

          {isEmpty(data) && <span style={{ marginLeft: 8 }}>Send image to chatGPT</span>}
        </Button>
      )}

      {!isEmpty(data) && !data.recognizeData && (
        <p style={{ color: 'red', fontWeight: 'bold' }}>Unable to detect information on image.</p>
      )}

      {!isEmpty(data) && !!data.recognizeData && (
        <Button
          disabled={loading}
          color="primary"
          variant="outlined"
          size="small"
          onClick={handleFillData}
          style={{ marginLeft: 16 }}
        >
          Autofill information
        </Button>
      )}

      {!isEmpty(data) && !!data.recognizeData && (
        <div style={{ maxWidth: '100%', overflowX: 'auto', marginTop: 8 }}>
          {Object.entries(data || {}).map(([field, value]) => {
            if (field === 'recognizeData') return null;

            return (
              <div key={field}>
                <FormControlLabel
                  label={
                    <p style={{ margin: 0, marginTop: 8 }}>
                      <span style={{ marginRight: 4 }}>
                        <b>{fieldNameMap[field] || ''}:</b>
                      </span>
                      <span>{value}</span>
                    </p>
                  }
                  control={
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={!!selectedFields[field]}
                      onChange={event => setSelectedFields({ ...selectedFields, [field]: event.target.checked })}
                    />
                  }
                  style={{ alignItems: 'start' }}
                />
              </div>
            );
          })}
        </div>
      )}
    </VisionTab>
  );
};

export default ChatGPT;
